'use client';
import MultiStepForm from '@/components/getStarted/multi-step-form/MultiStepForm';
import Image from 'next/image';
import clutchRatingLogo from '@/assets/images/clutch-rating.svg';
import { useState, use } from 'react';
import HalftoneContainer from '@/components/HalftoneContainer';
import { BrandsCarousel } from '@/components/getStarted/BrandsCarousel';
import { GetStartedCard } from '@/components/getStarted/GetStartedCard';
import { notFound } from 'next/navigation';
import { FormThankYou } from '@/components/getStarted/FormThankYou';
import {
  digitalProductEngineeringFormStepLabels,
  digitalProductEngineeringStepsConfig,
  appModernizationFormStepLabels,
  appModernizationStepsConfig,
  embeddedEngineeringTeamsFormStepLabels,
  embeddedEngineeringTeamsStepsConfig,
  IntakeFormId,
} from '@/components/getStarted/multi-step-form/multi-step-form.utils';
import clutchLogo from '@/assets/images/clutch.svg';
import goodFirmsLogo from '@/assets/images/goodfirms.svg';
import upcityLogo from '@/assets/images/upcity.svg';
import forbesLogo from '@/assets/images/forbes-council.svg';
import goodFirms2Logo from '@/assets/images/goodfirms2.svg';
import gcloudLogo from '@/assets/images/gcloud-partner.svg';
import BadgeShowcase from '@/components/getStarted/BadgeShowcase';

type Params = { uid: string };

const Page = (props: { params: Promise<Params> }) => {
  const params = use(props.params);

  const { uid } = params;

  const [currentStep, setCurrentStep] = useState(0);
  const [userName, setUserName] = useState('');

  const onStepChange = (state: number) => {
    setCurrentStep(state);
  };

  const onNameChange = (formData: string) => {
    setUserName(formData);
  };

  switch (uid) {
    case 'digital-product-engineering':
      switch (currentStep) {
        case 1:
          return (
            <HalftoneContainer className="min-h-screen md:px-8 px-5 pt-14 flex items-start justify-center">
              <MultiStepForm
                stepsConfig={digitalProductEngineeringStepsConfig}
                stepLabels={digitalProductEngineeringFormStepLabels}
                formType={IntakeFormId[uid]}
                onStepChange={onStepChange}
                onNameChange={onNameChange}
              />
              <div className="lg:max-w-md sm:max-w-md lg:flex hidden flex-col gap-10">
                <GetStartedCard
                  title="Why Choose Us?"
                  footerImg={
                    <Image
                      src={clutchRatingLogo}
                      sizes="100vw"
                      priority
                      alt="Clutch 5-star rating"
                      className="w-[80%] justify-self-center mt-4"
                      width={176}
                      height={62}
                    />
                  }
                >
                  <p className="text-sm">
                    Intelligent engineering solutions <br />
                    Home to custom strategy and execution, powered by in-house
                    consultancy expertise and the global Talent Cloud platform.
                  </p>
                  <p className="text-sm">
                    A global network of elite engineers <br />
                    With 10+ years of experience delivering scalable,
                    industry-specific solutions.
                  </p>
                  <p className="text-sm">
                    Proven results <br />
                    Recognized for timely delivery, measurable outcomes, and
                    staying ahead of the competition.
                  </p>
                  <p className="text-sm">
                    Seamless team extension <br />
                    Talent tailored to match your company&apos;s culture and
                    technical needs.
                  </p>
                </GetStartedCard>
              </div>
            </HalftoneContainer>
          );
        case 2:
          return <FormThankYou userName={userName} formVariant={uid} />;
        default:
          return (
            <HalftoneContainer className="min-h-screen md:px-8 px-5 pt-14 flex items-start justify-center">
              <MultiStepForm
                stepsConfig={digitalProductEngineeringStepsConfig}
                stepLabels={digitalProductEngineeringFormStepLabels}
                formType={IntakeFormId[uid]}
                onStepChange={onStepChange}
                onNameChange={onNameChange}
              />
              <div className="lg:max-w-md sm:max-w-md lg:flex hidden flex-col gap-10">
                <GetStartedCard
                  title="How Does Our Service Work?"
                  subTitle="We specialize in delivering cutting edge solutions to drive your goals forward:"
                >
                  <p>
                    We asses your goals. Gain an in-depth understanding of your
                    business and technical challenges.
                  </p>
                  <p>
                    We craft a custom plan. A tailored strategy with clear
                    milestones and deliverables.
                  </p>
                  <p>
                    We assemble a world-class team. Experts who execute
                    efficiently and deliver with precision.
                  </p>
                </GetStartedCard>
                <BrandsCarousel />
              </div>
            </HalftoneContainer>
          );
      }

    case 'application-modernization':
      switch (currentStep) {
        case 1:
          return (
            <HalftoneContainer className="min-h-screen md:px-8 px-5 pt-14 flex items-start justify-center">
              <MultiStepForm
                stepsConfig={appModernizationStepsConfig}
                stepLabels={appModernizationFormStepLabels}
                formType={IntakeFormId[uid]}
                onStepChange={onStepChange}
                onNameChange={onNameChange}
              />
              <div className="lg:max-w-md sm:max-w-md lg:flex hidden flex-col gap-10">
                <GetStartedCard
                  title="Why Choose Us?"
                  subTitle="We specialize in delivering cutting edge solutions to drive your goals forward:"
                  footerImg={
                    <Image
                      src={clutchRatingLogo}
                      sizes="100vw"
                      priority
                      alt="Clutch 5-star rating"
                      className="w-[80%] justify-self-center mt-4"
                      width={176}
                      height={62}
                    />
                  }
                >
                  <p>
                    <strong>Unmatched enterprise expertise</strong>
                    <br />
                    Our team has years of experience scaling enterprise
                    capabilities across industries.
                  </p>
                  <p>
                    <strong>Tailored solutions for growth</strong>
                    <br />
                    From cloud strategies to digital transformation, we deliver
                    solutions designed for your unique challenges.
                  </p>
                  <p>
                    <strong>Proven results you can trust</strong> <br />
                    Recognized for enabling growth, driving innovation, and
                    delivering on time with measurable outcomes.
                  </p>
                  <p>
                    <strong>Future-ready innovation</strong> <br />
                    Stay competitive with solutions designed to evolve with your
                    business.
                  </p>
                </GetStartedCard>
              </div>
            </HalftoneContainer>
          );
        case 2:
          return <FormThankYou userName={userName} formVariant={uid} />;
        default:
          return (
            <HalftoneContainer className="min-h-screen md:px-8 px-5 pt-14 flex items-start justify-center">
              <MultiStepForm
                stepsConfig={appModernizationStepsConfig}
                stepLabels={appModernizationFormStepLabels}
                formType={IntakeFormId[uid]}
                onStepChange={onStepChange}
                onNameChange={onNameChange}
              />
              <div className="lg:max-w-md sm:max-w-md lg:flex hidden flex-col gap-10">
                <GetStartedCard
                  title="How Does Our Service Work?"
                  subTitle="We specialize in delivering cutting edge solutions to drive your goals forward:"
                >
                  <p>
                    We asses your goals. Gain an in-depth understanding of your
                    business and technical challenges.
                  </p>
                  <p>
                    We craft a custom plan. A tailored strategy with clear
                    milestones and deliverables.
                  </p>
                  <p>
                    We assemble a world-class team. Experts who execute
                    efficiently and deliver with precision.
                  </p>
                </GetStartedCard>
                <BrandsCarousel />
              </div>
            </HalftoneContainer>
          );
      }

    case 'embedded-engineering-teams':
      switch (currentStep) {
        case 1:
          return (
            <HalftoneContainer className="min-h-screen md:px-8 px-5 pt-14 flex items-start justify-center">
              <MultiStepForm
                stepsConfig={embeddedEngineeringTeamsStepsConfig}
                stepLabels={embeddedEngineeringTeamsFormStepLabels}
                formType={IntakeFormId[uid]}
                onStepChange={onStepChange}
                onNameChange={onNameChange}
              />
              <div className="lg:max-w-md sm:max-w-md lg:flex hidden flex-col gap-10">
                <BadgeShowcase>
                  <p className="text-2xl font-bold">
                    {
                      "Our clients stay with us for an average of 6 years. Here's why:"
                    }
                  </p>
                  <Image
                    src={clutchLogo}
                    sizes="100vw"
                    priority
                    alt="Clutch"
                    className="w-[120%]"
                    width={82}
                    height={89}
                  />
                  <Image
                    src={goodFirmsLogo}
                    sizes="100vw"
                    priority
                    alt="Clutch 5-star rating"
                    className="w-[120%]"
                    width={82}
                    height={89}
                  />
                  <Image
                    src={upcityLogo}
                    sizes="100vw"
                    priority
                    alt="Clutch 5-star rating"
                    className="w-[120%]"
                    width={82}
                    height={89}
                  />
                  <Image
                    src={forbesLogo}
                    sizes="100vw"
                    priority
                    alt="Clutch 5-star rating"
                    className="w-[120%]"
                    width={82}
                    height={89}
                  />
                  <Image
                    src={goodFirms2Logo}
                    sizes="100vw"
                    priority
                    alt="Clutch 5-star rating"
                    className="w-[120%]"
                    width={82}
                    height={89}
                  />
                </BadgeShowcase>
              </div>
            </HalftoneContainer>
          );
        case 2:
          return <FormThankYou userName={userName} formVariant={uid} />;
        default:
          return (
            <HalftoneContainer className="min-h-screen md:px-8 px-5 pt-14 flex items-start justify-center">
              <MultiStepForm
                stepsConfig={embeddedEngineeringTeamsStepsConfig}
                stepLabels={embeddedEngineeringTeamsFormStepLabels}
                formType={IntakeFormId[uid]}
                onStepChange={onStepChange}
                onNameChange={onNameChange}
              />
              <div className="lg:max-w-md sm:max-w-md lg:flex hidden flex-col gap-10">
                <BadgeShowcase>
                  <p className="text-2xl font-bold">
                    Our awards are a testament to our quality and commitment
                    <span className="text-primary-green text-5xl leading-[0px]">
                      .
                    </span>{' '}
                  </p>
                  <Image
                    src={clutchRatingLogo}
                    sizes="100vw"
                    priority
                    alt="Clutch 5-star rating"
                    className="w-screen"
                    width={176}
                    height={62}
                  />
                  <Image
                    src={gcloudLogo}
                    sizes="100vw"
                    priority
                    alt="Google Cloud Partner"
                    className="w-screen"
                    width={176}
                    height={62}
                  />
                </BadgeShowcase>
              </div>
            </HalftoneContainer>
          );
      }
    default:
      return notFound();
  }
};

export default Page;
